import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Profile, CreateProfileDto, UpdateProfileDto, UpdateProfileRolesDto, ApiResponse, PaginatedResponse, OffboardProfileDto, OffboardingImpactSummary, OffboardingResult } from '../../../shared/types';
import axiosService from '../services/axios.service';
import { mentorshipKeys } from './useMentorships';

// Query key factory
export const profileKeys = {
    all: ['profiles'] as const,
    crews: (crewSlug: string) => [...profileKeys.all, 'crew', crewSlug] as const,
    list: (crewSlug: string, page?: number, limit?: number, search?: string, filters?: {
        roles?: string[];
        isMentor?: boolean;
        hasDirectReports?: boolean;
    }) =>
        [...profileKeys.crews(crewSlug), 'list', { page, limit, search, filters }] as const,
    detail: (crewSlug: string, profileHandle: string) =>
        [...profileKeys.crews(crewSlug), 'detail', profileHandle] as const,
    checkId: (crewSlug: string, profileHandle: string) =>
        [...profileKeys.crews(crewSlug), 'check-id', profileHandle] as const,
    autocomplete: (crewSlug: string, search: string, includeIds?: string[], excludeIds?: string[]) =>
        [...profileKeys.crews(crewSlug), 'autocomplete', { search, includeIds, excludeIds }] as const
};

// API functions
const profileApi = {
    getProfiles: async (
        crewSlug: string,
        page: number = 1,
        limit: number = 10,
        search: string = '',
        filters?: {
            roles?: string[];
            isMentor?: boolean;
            hasDirectReports?: boolean;
            isActive?: boolean;
        }
    ): Promise<PaginatedResponse<Profile>> => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic params object for axios
        const params: any = { page, limit };
        if (search) {
            params.search = search;
        }
        if (filters?.roles && filters.roles.length > 0) {
            params.roles = filters.roles.join(',');
        }
        if (filters?.isMentor !== undefined) {
            params.isMentor = filters.isMentor;
        }
        if (filters?.hasDirectReports !== undefined) {
            params.hasDirectReports = filters.hasDirectReports;
        }
        if (filters?.isActive !== undefined) {
            params.isActive = filters.isActive;
        }
        const { data } = await axiosService.get<PaginatedResponse<Profile>>(`/c/${crewSlug}/profiles`, {
            params
        });
        return data;
    },

    getProfile: async (crewSlug: string, profileHandle: string): Promise<ApiResponse<Profile>> => {
        const { data } = await axiosService.get<ApiResponse<Profile>>(`/c/${crewSlug}/profiles/p/${profileHandle}`);
        return data;
    },

    createProfile: async (crewSlug: string, profileData: CreateProfileDto): Promise<ApiResponse<Profile>> => {
        const { data } = await axiosService.post<ApiResponse<Profile>>(`/c/${crewSlug}/profiles`, profileData);
        return data;
    },

    updateProfile: async (crewSlug: string, profileHandle: string, profileData: UpdateProfileDto): Promise<ApiResponse<Profile>> => {
        const { data } = await axiosService.put<ApiResponse<Profile>>(`/c/${crewSlug}/profiles/p/${profileHandle}`, profileData);
        return data;
    },

    updateProfileRoles: async (crewSlug: string, profileHandle: string, roles: UpdateProfileRolesDto): Promise<ApiResponse<Profile>> => {
        const { data } = await axiosService.patch<ApiResponse<Profile>>(`/c/${crewSlug}/profiles/p/${profileHandle}/roles`, roles);
        return data;
    },

    deleteProfile: async (crewSlug: string, profileHandle: string): Promise<ApiResponse<void>> => {
        const { data } = await axiosService.delete<ApiResponse<void>>(`/c/${crewSlug}/profiles/p/${profileHandle}`);
        return data;
    },

    hardDeleteProfile: async (crewSlug: string, profileHandle: string): Promise<ApiResponse<void>> => {
        const { data } = await axiosService.delete<ApiResponse<void>>(`/c/${crewSlug}/profiles/p/${profileHandle}/hard`);
        return data;
    },

    checkIdAvailability: async (crewSlug: string, profileHandle: string): Promise<ApiResponse<{ available: boolean }>> => {
        const { data } = await axiosService.get<ApiResponse<{ available: boolean }>>(`/c/${crewSlug}/profiles/check-id/${profileHandle}`);
        return data;
    },

    countProfilesByCrew: async (crewSlug: string, excludePerseraAdmins: boolean = false): Promise<ApiResponse<{ count: number }>> => {
        const params = excludePerseraAdmins ? { excludePerseraAdmins: 'true' } : {};
        const { data } = await axiosService.get<ApiResponse<{ count: number }>>(`/c/${crewSlug}/profiles/count`, { params });
        return data;
    },

    getAllCrewProfiles: async (crewSlug: string): Promise<ApiResponse<Profile[]>> => {
        const { data } = await axiosService.get<ApiResponse<Profile[]>>(`/c/${crewSlug}/profiles/all`);
        return data;
    },

    findByIds: async (crewSlug: string, profileIds: string[]): Promise<ApiResponse<Profile[]>> => {
        const { data } = await axiosService.post<ApiResponse<Profile[]>>(
            `/c/${crewSlug}/profiles/find-by-ids`,
            { profileIds }
        );
        return data;
    },
    findByEmails: async (crewSlug: string, emails: string[]): Promise<ApiResponse<Profile[]>> => {
        const { data } = await axiosService.post<ApiResponse<Profile[]>>(
            `/c/${crewSlug}/profiles/find-by-emails`,
            { emails }
        );
        return data;
    },

    autocompleteProfiles: async (
        crewSlug: string,
        search: string = '',
        limit: number = 20,
        includeIds?: string[],
        excludeIds?: string[]
    ): Promise<ApiResponse<Profile[]>> => {
        const params: Record<string, string | number> = { limit };

        if (search) {
            params.q = search;
        }
        if (includeIds && includeIds.length > 0) {
            params.includeIds = includeIds.join(',');
        }
        if (excludeIds && excludeIds.length > 0) {
            params.excludeIds = excludeIds.join(',');
        }

        const { data } = await axiosService.get<ApiResponse<Profile[]>>(
            `/c/${crewSlug}/profiles/autocomplete`,
            { params }
        );
        return data;
    }
};

// React Query hooks
// useProfiles is for paginated listings, the limit should not exceed 100
export const useProfiles = (
    crewSlug: string,
    page: number = 1,
    limit: number = 10,
    search: string = '',
    filters?: {
        roles?: string[];
        isMentor?: boolean;
        hasDirectReports?: boolean;
        isActive?: boolean;
    }
) => {
    return useQuery({
        queryKey: profileKeys.list(crewSlug, page, limit, search, filters),
        queryFn: () => profileApi.getProfiles(crewSlug, page, limit, search, filters),
        enabled: !!crewSlug,
        staleTime: 120000 // Consider data fresh for 2 minutes
    });
};

export const useFindProfilesByEmail = (crewSlug: string, emails: string[]) => {
    return useQuery({
        queryKey: ['profiles', 'find-by-email', crewSlug, emails],
        queryFn: () => {
            return profileApi.findByEmails(crewSlug, emails);
        }
    });
};

const FIND_BY_IDS_BATCH_SIZE = 200;

export const useFindProfilesByIds = (crewSlug: string, profileIds: string[], excludeIds?: string[]) => {
    return useQuery({
        queryKey: ['profiles', 'find-by-ids', crewSlug, profileIds, excludeIds],
        queryFn: async () => {
            const toFetch = profileIds.filter(id => !excludeIds?.includes(id));
            if (toFetch.length === 0) {
                return { data: [] } as any as ApiResponse<Profile[]>;
            }
            if (toFetch.length <= FIND_BY_IDS_BATCH_SIZE) {
                return profileApi.findByIds(crewSlug, toFetch);
            }
            // Split into batches and merge results
            const batches: string[][] = [];
            for (let i = 0; i < toFetch.length; i += FIND_BY_IDS_BATCH_SIZE) {
                batches.push(toFetch.slice(i, i + FIND_BY_IDS_BATCH_SIZE));
            }
            const results = await Promise.all(batches.map(batch => profileApi.findByIds(crewSlug, batch)));
            return {
                success: true,
                data: results.flatMap(r => r.data ?? [])
            } as ApiResponse<Profile[]>;
        }
    });
};

export const useProfile = (crewSlug: string, identifier: string) => {
    return useQuery({
        queryKey: profileKeys.detail(crewSlug, identifier),
        queryFn: () => profileApi.getProfile(crewSlug, identifier),
        enabled: !!crewSlug && !!identifier
    });
};

export const useCheckprofileHandle = (crewSlug: string, profileHandle: string) => {
    return useQuery({
        queryKey: profileKeys.checkId(crewSlug, profileHandle),
        queryFn: () => profileApi.checkIdAvailability(crewSlug, profileHandle),
        enabled: !!crewSlug && !!profileHandle && profileHandle.length > 0
    });
};

export const useCreateProfile = (crewSlug: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (profileData: CreateProfileDto) => profileApi.createProfile(crewSlug, profileData),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: profileKeys.crews(crewSlug) });
        }
    });
};

export const useUpdateProfile = (crewSlug: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ profileHandle, data }: { profileHandle: string; data: UpdateProfileDto }) =>
            profileApi.updateProfile(crewSlug, profileHandle, data),
        onSuccess: (_, { profileHandle }) => {
            queryClient.invalidateQueries({ queryKey: profileKeys.detail(crewSlug, profileHandle) });
            queryClient.invalidateQueries({ queryKey: profileKeys.crews(crewSlug) });
        }
    });
};

export const useUpdateProfileRoles = (crewSlug: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ profileHandle, roles }: { profileHandle: string; roles: UpdateProfileRolesDto }) =>
            profileApi.updateProfileRoles(crewSlug, profileHandle, roles),
        onSuccess: (_, { profileHandle }) => {
            queryClient.invalidateQueries({ queryKey: profileKeys.detail(crewSlug, profileHandle) });
            queryClient.invalidateQueries({ queryKey: profileKeys.crews(crewSlug) });
            queryClient.invalidateQueries({ queryKey: ['crewAdmins', crewSlug] });
        }
    });
};

export const useDeleteProfile = (crewSlug: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (profileHandle: string) => profileApi.deleteProfile(crewSlug, profileHandle),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: profileKeys.crews(crewSlug) });
        }
    });
};

export const useHardDeleteProfile = (crewSlug: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (profileHandle: string) => profileApi.hardDeleteProfile(crewSlug, profileHandle),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: profileKeys.crews(crewSlug) });
        }
    });
};

export const useLeaveCrew = (crewSlug: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => axiosService.delete(`/c/${crewSlug}/profiles/me`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['crews', 'detail', crewSlug] });
            queryClient.invalidateQueries({ queryKey: ['crewMember', crewSlug] });
            queryClient.invalidateQueries({ queryKey: ['crews', 'list'] });
        }
    });
};

export const useCountProfilesByCrew = (crewSlug: string, excludePerseraAdmins: boolean = false) => {
    return useQuery({
        queryKey: ['profiles', 'count', crewSlug, excludePerseraAdmins],
        queryFn: () => profileApi.countProfilesByCrew(crewSlug, excludePerseraAdmins),
        enabled: !!crewSlug
    });
};

/**
 * Hook for fetching all crew profiles with minimal fields
 * Returns only active profiles with: _id, displayName, handle, profilePicture, businessTitle, department, isActive
 * Use this instead of useProfiles with large limits when you need all profiles
 */
export const useCrewProfiles = (crewSlug: string) => {
    return useQuery({
        queryKey: ['profiles', 'all', crewSlug],
        queryFn: () => profileApi.getAllCrewProfiles(crewSlug),
        enabled: !!crewSlug,
        staleTime: 300000 // Consider data fresh for 5 minutes
    });
};

/**
 * Hook for profile autocomplete - optimized for large crews
 * Supports inclusion/exclusion filters and returns fewer fields for performance
 * @param crewSlug - The crew slug
 * @param search - Search query string
 * @param options - Optional configuration
 * @param options.limit - Maximum results to return (default: 20, max: 50)
 * @param options.includeIds - Array of profile IDs to include (whitelist)
 * @param options.excludeIds - Array of profile IDs to exclude (blacklist)
 * @param options.enabled - Whether the query should run (default: true)
 */
export const useProfileAutocomplete = (
    crewSlug: string,
    search: string = '',
    options: {
        limit?: number;
        includeIds?: string[];
        excludeIds?: string[];
        enabled?: boolean;
    } = {}
) => {
    const { limit = 20, includeIds, excludeIds, enabled = true } = options;
    const trimmedSearch = search.trim();

    return useQuery({
        queryKey: profileKeys.autocomplete(crewSlug, trimmedSearch, includeIds, excludeIds),
        queryFn: () => profileApi.autocompleteProfiles(crewSlug, trimmedSearch, limit, includeIds, excludeIds),
        enabled: !!crewSlug && enabled,
        staleTime: 30000, // Consider data fresh for 30 seconds
        placeholderData: (previousData) => previousData // Keep previous data while fetching
    });
};

/**
 * Hook to get offboarding impact summary for a profile
 */
export const useOffboardingImpact = (crewSlug: string, profileId: string, enabled: boolean = true) => {
    return useQuery({
        queryKey: ['profiles', 'offboarding-impact', crewSlug, profileId],
        queryFn: async (): Promise<OffboardingImpactSummary> => {
            const { data } = await axiosService.get<ApiResponse<OffboardingImpactSummary>>(
                `/c/${crewSlug}/profiles/${profileId}/offboarding-impact`
            );
            return data.data!;
        },
        enabled: !!crewSlug && !!profileId && enabled,
        staleTime: 0 // Always fetch fresh data
    });
};

/**
 * Hook to offboard a profile
 */
export const useOffboardProfile = (crewSlug: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: OffboardProfileDto): Promise<OffboardingResult> => {
            const { data: responseData } = await axiosService.post<ApiResponse<OffboardingResult>>(
                `/c/${crewSlug}/profiles/${data.profileId}/offboard`,
                data
            );
            return responseData.data!;
        },
        onSuccess: (_, variables) => {
            // Invalidate all profile-related queries
            queryClient.invalidateQueries({ queryKey: profileKeys.crews(crewSlug) });
            queryClient.invalidateQueries({ queryKey: ['profiles', 'offboarding-impact', crewSlug, variables.profileId] });
            // Invalidate manager relationships
            queryClient.invalidateQueries({ queryKey: ['manager-relationships', crewSlug] });
            // Invalidate mentorships
            queryClient.invalidateQueries({ queryKey: mentorshipKeys.crews(crewSlug) });
        }
    });
};

/**
 * Hook to reactivate an offboarded profile
 */
export const useReactivateProfile = (crewSlug: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (profileId: string): Promise<Profile> => {
            const { data } = await axiosService.post<ApiResponse<Profile>>(
                `/c/${crewSlug}/profiles/${profileId}/reactivate`
            );
            return data.data!;
        },
        onSuccess: (_, profileId) => {
            // Invalidate all profile-related queries
            queryClient.invalidateQueries({ queryKey: profileKeys.crews(crewSlug) });
            queryClient.invalidateQueries({ queryKey: ['profiles', 'offboarding-impact', crewSlug, profileId] });
        }
    });
};
